/**
 * Watchpoint (monitor) hooks — list/get/create/update/delete/toggle + uptime
 * stats and probe-result history. All scoped by `project_id` (from the route).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  healthcheckApi,
  hcKeys,
  unwrap,
  unwrapList,
  type Watchpoint,
  type WatchpointType,
  type ProbeResult,
  type UptimeStats,
  type BaselineResponse,
  type ListPagination,
} from './_shared';

export interface WatchpointListFilters {
  type?: WatchpointType;
  page?: number;
  page_size?: number;
}

export interface WatchpointListResult {
  items: Watchpoint[];
  pagination?: ListPagination;
}

/** List watchpoints for a project. */
export function useWatchpoints(
  projectId: string | undefined,
  filters: WatchpointListFilters = {},
) {
  const params = { project_id: projectId, ...filters };
  return useQuery<WatchpointListResult>({
    queryKey: hcKeys.watchpoints(projectId ?? '', filters as Record<string, unknown>),
    enabled: !!projectId,
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await healthcheckApi.get('/watchpoints', { params });
      return unwrapList<Watchpoint>(res);
    },
  });
}

/** Fetch a single watchpoint. */
export function useWatchpoint(id: string | undefined) {
  return useQuery<Watchpoint>({
    queryKey: hcKeys.watchpoint(id ?? ''),
    enabled: !!id,
    queryFn: async () => unwrap<Watchpoint>(await healthcheckApi.get(`/watchpoints/${id}`)),
  });
}

/**
 * Uptime aggregate over a window (default last 24h).
 * BE `parseWatchpointTimeRange` requires a leading `-` prefix (e.g. `-24h`).
 * Bare `24h` is silently treated as an invalid duration and falls back to `-1h`.
 */
export function useWatchpointStats(id: string | undefined, since = '-24h') {
  return useQuery<UptimeStats>({
    queryKey: hcKeys.watchpointStats(id ?? '', { since }),
    enabled: !!id,
    refetchInterval: 60_000,
    queryFn: async () =>
      unwrap<UptimeStats>(
        await healthcheckApi.get(`/watchpoints/${id}/stats`, { params: { since } }),
      ),
  });
}

/**
 * BE `parseWatchpointTimeRange` accepts two formats:
 *   - Duration with leading `-`: `-1h`, `-24h`, `-7d` (relative to now)
 *   - RFC3339 absolute: `2026-01-01T00:00:00Z`
 * `to` defaults to `"now"` server-side when omitted.
 * When `from`/`to` are both omitted, BE defaults `from` to `-1h`.
 */
export interface MetricsRange {
  from?: string;
  to?: string;
  page_size?: number;
}

/** Probe-result history (newest first), for the latency chart + history table. */
export function useWatchpointMetrics(id: string | undefined, range: MetricsRange = {}) {
  const params = { page_size: 200, ...range };
  return useQuery<ProbeResult[]>({
    queryKey: hcKeys.watchpointMetrics(id ?? '', range as Record<string, unknown>),
    enabled: !!id,
    refetchInterval: 60_000,
    queryFn: async () => unwrapList<ProbeResult>(
      await healthcheckApi.get(`/watchpoints/${id}/metrics`, { params }),
    ).items,
  });
}

/**
 * 7-day anomaly baseline + suggested thresholds (`GET /watchpoints/:id/baseline`).
 * Recomputed hourly server-side; `status: 'insufficient_data'` until enough samples.
 */
export function useWatchpointBaseline(id: string | undefined) {
  return useQuery<BaselineResponse>({
    queryKey: hcKeys.watchpointBaseline(id ?? ''),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () =>
      unwrap<BaselineResponse>(await healthcheckApi.get(`/watchpoints/${id}/baseline`)),
  });
}

export interface CreateWatchpointInput {
  project_id: string;
  name: string;
  type: WatchpointType;
  config: Record<string, unknown>;
  check_interval: number;
  timeout_seconds: number;
}

export function useCreateWatchpoint(
  projectId: string,
  options?: UseMutationOptions<Watchpoint, Error, CreateWatchpointInput>,
) {
  const qc = useQueryClient();
  return useMutation<Watchpoint, Error, CreateWatchpointInput>({
    mutationFn: async (input) =>
      unwrap<Watchpoint>(await healthcheckApi.post('/watchpoints', input)),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['hc', projectId, 'watchpoints'] });
      options?.onSuccess?.(...args);
    },
  });
}

export interface UpdateWatchpointInput {
  name?: string;
  config?: Record<string, unknown>;
  check_interval?: number;
  timeout_seconds?: number;
  is_active?: boolean;
}

export function useUpdateWatchpoint(
  id: string,
  projectId: string,
  options?: UseMutationOptions<Watchpoint, Error, UpdateWatchpointInput>,
) {
  const qc = useQueryClient();
  return useMutation<Watchpoint, Error, UpdateWatchpointInput>({
    mutationFn: async (input) =>
      unwrap<Watchpoint>(await healthcheckApi.put(`/watchpoints/${id}`, input)),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: hcKeys.watchpoint(id) });
      qc.invalidateQueries({ queryKey: ['hc', projectId, 'watchpoints'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Enable/disable a monitor (`PATCH /watchpoints/:id/active`). */
export function useToggleWatchpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; is_active: boolean }>({
    mutationFn: async ({ id, is_active }) => {
      await healthcheckApi.patch(`/watchpoints/${id}/active`, { is_active });
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: hcKeys.watchpoint(id) });
      qc.invalidateQueries({ queryKey: ['hc', projectId, 'watchpoints'] });
    },
  });
}

export function useDeleteWatchpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await healthcheckApi.delete(`/watchpoints/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hc', projectId, 'watchpoints'] }),
  });
}

/** One-off test fetch — runs a config once without persisting (create preview). */
export function useTestWatchpoint() {
  return useMutation<unknown, Error, { type: WatchpointType; config: Record<string, unknown> }>({
    mutationFn: async (input) => unwrap(await healthcheckApi.post('/watchpoints/test', input)),
  });
}
